import { useState, useEffect } from 'react';

const data = [
  { label: 'Liquidity', pct: 45, desc: 'Allocated for liquidity provision' },
  { label: 'Xeno Reserve', pct: 5, desc: 'Reserved for future use and treasury purposes' },
  { label: 'Strategic Raise', pct: 20, desc: 'Allocated for strategic fundraising initiatives' },
  { label: 'Community / Growth', pct: 10, desc: 'Reserved for community incentives, growth, and ecosystem expansion' },
  { label: 'Marketing & Operations', pct: 15, desc: 'Reserved for marketing campaigns and operational expenses' },
  { label: 'Advisors', pct: 5, desc: 'Allocated for advisors and strategic partners' },
];

// Generate pastel colors based on #170733
const generatePastelColors = (baseColor, count) => {
  const colors = [];
  const base = { h: 249, s: 70, l: 12 }; // HSL values for #170733
  
  for (let i = 0; i < count; i++) {
    const hue = (base.h + (i * 25)) % 360;
    const saturation = Math.max(20, base.s - (i * 5));
    const lightness = Math.min(85, 65 + (i * 3));
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }
  return colors;
};

// utility: describe an arc path in SVG
function describeArc(x, y, radius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    `M ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    `L ${x} ${y}`,
    'Z',
  ].join(' ');
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// Calculate label position with responsive adjustment
function getLabelPosition(cx, cy, radius, startAngle, endAngle, isMobile = false) {
  const midAngle = (startAngle + endAngle) / 2;
  const labelRadius = radius + (isMobile ? 25 : 40);
  return polarToCartesian(cx, cy, labelRadius, midAngle);
}

export default function ResponsiveNeonPieChart() {
  const [hover, setHover] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Responsive dimensions
  const containerWidth = isMobile ? 320 : 600;
  const containerHeight = isMobile ? 300 : 500;
  const svgSize = isMobile ? 300 : 500;
  const center = svgSize / 2;
  const radius = isMobile ? 120 : 180;
  const svgLeft = isMobile ? 10 : 50;

  const colors = generatePastelColors('#170733', data.length);
  let cumulative = 0;

  const segments = data.map((seg, i) => {
    const startAngle = (cumulative / 100) * 360;
    const endAngle = ((cumulative + seg.pct) / 100) * 360;
    const labelPos = getLabelPosition(center, center, radius, startAngle, endAngle, isMobile);
    const color = colors[i];
    
    cumulative += seg.pct;
    
    return {
      ...seg,
      startAngle,
      endAngle,
      labelPos,
      color,
      index: i
    };
  });

  const containerStyle = {
    position: 'relative',
    width: `${containerWidth}px`,
    height: `${containerHeight}px`,
    margin: 'auto',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
    borderRadius: '20px',
    padding: '20px',
    boxShadow: '0 20px 40px rgba(23, 7, 51, 0.3)',
    maxWidth: '95vw',
    boxSizing: 'border-box'
  };

  const segmentStyle = (segment, isHovered) => ({
    fill: segment.color,
    opacity: isHovered ? 1 : 0.8,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    filter: isHovered 
      ? `drop-shadow(0 0 ${isMobile ? '8px' : '12px'} ${segment.color}) drop-shadow(0 0 ${isMobile ? '16px' : '24px'} ${segment.color})` 
      : 'none'
  });

  const labelStyle = (segment, isHovered) => ({
    position: 'absolute',
    left: `${segment.labelPos.x + svgLeft}px`,
    top: `${segment.labelPos.y}px`,
    transform: 'translate(-50%, -50%)',
    color: isHovered ? '#ffffff' : '#b8b8d4',
    fontSize: isMobile ? '11px' : '13px',
    fontWeight: isHovered ? '600' : '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    minWidth: isMobile ? '80px' : '100px',
    padding: isMobile ? '6px 8px' : '8px 12px',
    borderRadius: '8px',
    backgroundColor: isHovered ? 'rgba(23, 7, 51, 0.9)' : 'rgba(23, 7, 51, 0.6)',
    border: isHovered ? `2px solid ${segment.color}` : '2px solid transparent',
    backdropFilter: 'blur(10px)',
    boxShadow: isHovered 
      ? `0 0 20px ${segment.color}40, 0 8px 16px rgba(0,0,0,0.3)` 
      : '0 4px 8px rgba(0,0,0,0.2)',
    zIndex: isHovered ? 3 : 2
  });

  const tooltipStyle = hover !== null ? {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'linear-gradient(135deg, rgba(23, 7, 51, 0.95), rgba(23, 7, 51, 0.8))',
    color: '#ffffff',
    padding: isMobile ? '12px 16px' : '16px 20px',
    borderRadius: '12px',
    pointerEvents: 'none',
    textAlign: 'center',
    maxWidth: isMobile ? '200px' : '250px',
    border: `2px solid ${segments[hover].color}`,
    boxShadow: `0 0 30px ${segments[hover].color}40, 0 12px 24px rgba(0,0,0,0.4)`,
    backdropFilter: 'blur(15px)',
    zIndex: 10,
    fontSize: isMobile ? '12px' : '14px'
  } : {};

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div style={containerStyle}>
        <svg 
          width={svgSize} 
          height={svgSize} 
          viewBox={`0 0 ${svgSize} ${svgSize}`} 
          style={{ 
            position: 'absolute', 
            left: `${svgLeft}px`, 
            top: '0px'
          }}
        >
          {segments.map((segment) => (
            <path
              key={segment.index}
              d={describeArc(center, center, radius, segment.startAngle, segment.endAngle)}
              style={segmentStyle(segment, hover === segment.index)}
              onMouseEnter={() => setHover(segment.index)}
              onMouseLeave={() => setHover(null)}
              onTouchStart={() => setHover(segment.index)}
              onTouchEnd={() => setTimeout(() => setHover(null), 2000)}
            />
          ))}
        </svg>

        {segments.map((segment) => (
          <div
            key={`label-${segment.index}`}
            style={labelStyle(segment, hover === segment.index)}
            onMouseEnter={() => setHover(segment.index)}
            onMouseLeave={() => setHover(null)}
            onTouchStart={() => setHover(segment.index)}
            onTouchEnd={() => setTimeout(() => setHover(null), 2000)}
          >
            <div style={{ 
              fontWeight: '600', 
              marginBottom: '2px',
              fontSize: isMobile ? '10px' : '12px'
            }}>
              {segment.label}
            </div>
            <div style={{ 
              fontSize: isMobile ? '9px' : '11px', 
              opacity: '0.9' 
            }}>
              {segment.pct}%
            </div>
          </div>
        ))}

        {hover !== null && (
          <div style={tooltipStyle}>
            <div style={{ 
              fontWeight: '700', 
              fontSize: isMobile ? '14px' : '16px', 
              marginBottom: '8px', 
              color: segments[hover].color 
            }}>
              {segments[hover].label}
            </div>
            <div style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              marginBottom: '4px' 
            }}>
              {segments[hover].pct}%
            </div>
            <div style={{ 
              fontSize: isMobile ? '10px' : '12px', 
              opacity: '0.9', 
              lineHeight: '1.4' 
            }}>
              {segments[hover].desc}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}